import * as yup from 'yup';
import {
  DecubitusStadium,
  FootSyndromGrade,
  UlcusGrade,
  values,
  WoundDescriptionType,
  WoundType,
} from '../../../enums';

export const WoundDescriptionSchema = yup.object({
  description: yup.string().nullable(),
  dimensionLength: yup.mixed().when('description', {
    is: (value: string) => !value,
    then: yup.number().required('Angabe erforderlich'),
    otherwise: yup.number().nullable(),
  }),
  dimensionsWidth: yup.mixed().when('description', {
    is: (value: string) => !value,
    then: yup.number().required('Angabe erforderlich'),
    otherwise: yup.number().nullable(),
  }),
  dimensionsDepth: yup.number().nullable(),
  woundType: yup.mixed<WoundType>().oneOf(Object.values(WoundType)).required('Angabe erforderlich'),
  decubitusStadium: yup.mixed<DecubitusStadium | null>().when('woundType', {
    is: WoundType.DECUBITUS,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf(values(DecubitusStadium), 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([...values(DecubitusStadium), null]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  decubitusStadiumDescription: yup
    .string()
    .optional()
    .when('decubitusStadium', {
      is: DecubitusStadium.OTHER,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup.string().required('Angabe erforderlich'),
        otherwise: yup.string().nullable(),
      }),
      otherwise: yup.string().nullable(),
    }),
  ulcusGrade: yup.mixed<UlcusGrade>().when('woundType', {
    is: (value: string) =>
      value === WoundType.ULCUSCRURISDIABET || value === WoundType.ULCUSCRURISVEN,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .string()
        .oneOf([...values(UlcusGrade), null], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup
        .mixed()
        .oneOf([
          UlcusGrade.ONE,
          UlcusGrade.TWO,
          UlcusGrade.THREE,
          UlcusGrade.FOUR,
          UlcusGrade.FIVE,
          UlcusGrade.SIX,
          null,
        ]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  footSyndromGrade: yup.mixed<FootSyndromGrade>().when('woundType', {
    is: WoundType.FOOTSYNDROM,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .string()
        .oneOf([
          FootSyndromGrade.ONE,
          FootSyndromGrade.TWO,
          FootSyndromGrade.THREE,
          FootSyndromGrade.FOUR,
          FootSyndromGrade.FIVE,
        ])
        .required(),
      otherwise: yup
        .mixed()
        .oneOf([
          FootSyndromGrade.ONE,
          FootSyndromGrade.TWO,
          FootSyndromGrade.THREE,
          FootSyndromGrade.FOUR,
          FootSyndromGrade.FIVE,
          null,
        ]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  surroundingA: yup.mixed<WoundDescriptionType>().when('description', {
    is: (value: string) => !value,
    then: yup
      .mixed()
      .oneOf(
        [WoundDescriptionType.WHITE, WoundDescriptionType.ROSY, WoundDescriptionType.REDDENED],
        'Angabe erforderlich',
      )
      .required('Angabe erforderlich'),
    otherwise: yup
      .mixed()
      .oneOf([
        WoundDescriptionType.WHITE,
        WoundDescriptionType.ROSY,
        WoundDescriptionType.REDDENED,
        null,
      ]),
  }),
  surroundingB: yup.mixed<WoundDescriptionType>().when('description', {
    is: (value: string) => !value,
    then: yup
      .mixed()
      .oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY], 'Angabe erforderlich')
      .required('Angabe erforderlich'),
    otherwise: yup.mixed().oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY, null]),
  }),
  borderA: yup.mixed<WoundDescriptionType>().when('description', {
    is: (value: string) => !value,
    then: yup
      .mixed()
      .oneOf([WoundDescriptionType.ROSY, WoundDescriptionType.RED], 'Angabe erforderlich')
      .required('Angabe erforderlich'),
    otherwise: yup.mixed().oneOf([WoundDescriptionType.ROSY, WoundDescriptionType.RED, null]),
  }),
  borderB: yup.mixed<WoundDescriptionType>().when('description', {
    is: (value: string) => !value,
    then: yup
      .mixed()
      .oneOf([WoundDescriptionType.SOFT, WoundDescriptionType.HARD], 'Angabe erforderlich')
      .required('Angabe erforderlich'),
    otherwise: yup.mixed().oneOf([WoundDescriptionType.SOFT, WoundDescriptionType.HARD, null]),
  }),
  borderPainful: yup.boolean().default(false),
  borderSublime: yup.boolean().default(false),
  exsudate: yup.boolean().default(false),
  exsudateDescriptionA: yup.mixed<WoundDescriptionType | null>().when('exsudate', {
    is: true,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf([WoundDescriptionType.LITTLE, WoundDescriptionType.MUCH], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([WoundDescriptionType.LITTLE, WoundDescriptionType.MUCH, null]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  exsudateDescriptionB: yup.mixed<WoundDescriptionType | null>().when('exsudate', {
    is: true,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf(
          [WoundDescriptionType.CLEAR, WoundDescriptionType.SERIOUS, WoundDescriptionType.BLOODY],
          'Angabe erforderlich',
        )
        .required('Angabe erforderlich'),
      otherwise: yup
        .mixed()
        .oneOf([
          WoundDescriptionType.CLEAR,
          WoundDescriptionType.SERIOUS,
          WoundDescriptionType.BLOODY,
          null,
        ]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  necrosis: yup.boolean().default(false),
  necrosisDescriptionA: yup.mixed<WoundDescriptionType>().when('necrosis', {
    is: true,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY, null]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  necrosisDescriptionB: yup.mixed<WoundDescriptionType>().when('necrosis', {
    is: true,
    then: yup.mixed().when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf(
          [WoundDescriptionType.PARTIALLY, WoundDescriptionType.COMPLETE],
          'Angabe erforderlich',
        )
        .required('Angabe erforderlich'),
      otherwise: yup
        .mixed()
        .oneOf([WoundDescriptionType.PARTIALLY, WoundDescriptionType.COMPLETE, null]),
    }),
    otherwise: yup.mixed().oneOf([null]),
  }),
  foulSmelling: yup.boolean().default(false),
  note: yup.string().nullable(),
  documentedAt: yup.date().required(),
});

export const UpdateWoundDescriptionSchema = yup.object({
  description: yup.string().nullable(),
  dimensionLength: yup
    .mixed()
    .when('description', {
      is: (value: string) => !value,
      then: yup.number().required('Angabe erforderlich'),
      otherwise: yup.number().nullable(),
    })
    .nullable(),
  dimensionsWidth: yup
    .mixed()
    .when('description', {
      is: (value: string) => !value,
      then: yup.number().required('Angabe erforderlich'),
      otherwise: yup.number().nullable(),
    })
    .nullable(),
  dimensionsDepth: yup.number().nullable(),
  woundType: yup.mixed<WoundType>().oneOf(Object.values(WoundType)).required('Angabe erforderlich'),
  decubitusStadium: yup.mixed<DecubitusStadium | null>().when('woundType', {
    is: WoundType.DECUBITUS,
    then: yup
      .mixed()
      .when('description', {
        is: (value: string) => !value,
        then: yup
          .mixed()
          .oneOf(values(DecubitusStadium), 'Angabe erforderlich')
          .required('Angabe erforderlich'),
        otherwise: yup.mixed().oneOf([...values(DecubitusStadium), null]),
      })
      .nullable(),
    otherwise: yup.mixed().oneOf([null]),
  }),
  decubitusStadiumDescription: yup
    .string()
    .optional()
    .when('decubitusStadium', {
      is: DecubitusStadium.OTHER,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup.string().required('Angabe erforderlich'),
        otherwise: yup.string().nullable(),
      }),
      otherwise: yup.string().nullable(),
    })
    .nullable(),
  ulcusGrade: yup
    .mixed<UlcusGrade>()
    .when('woundType', {
      is: (value: string) =>
        value === WoundType.ULCUSCRURISDIABET || value === WoundType.ULCUSCRURISVEN,
      then: yup
        .mixed()
        .when('description', {
          is: (value: string) => !value,
          then: yup
            .string()
            .oneOf([...values(UlcusGrade), null], 'Angabe erforderlich')
            .required('Angabe erforderlich'),
          otherwise: yup
            .mixed()
            .oneOf([
              UlcusGrade.ONE,
              UlcusGrade.TWO,
              UlcusGrade.THREE,
              UlcusGrade.FOUR,
              UlcusGrade.FIVE,
              UlcusGrade.SIX,
              null,
            ]),
        })
        .nullable(),
      otherwise: yup.mixed().oneOf([null]),
    })
    .nullable(),
  footSyndromGrade: yup
    .mixed<FootSyndromGrade>()
    .when('woundType', {
      is: WoundType.FOOTSYNDROM,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup
          .string()
          .oneOf([
            FootSyndromGrade.ONE,
            FootSyndromGrade.TWO,
            FootSyndromGrade.THREE,
            FootSyndromGrade.FOUR,
            FootSyndromGrade.FIVE,
          ])
          .required(),
        otherwise: yup
          .mixed()
          .oneOf([
            FootSyndromGrade.ONE,
            FootSyndromGrade.TWO,
            FootSyndromGrade.THREE,
            FootSyndromGrade.FOUR,
            FootSyndromGrade.FIVE,
            null,
          ]),
      }),
      otherwise: yup.mixed().oneOf([null]),
    })
    .nullable(),
  surroundingA: yup
    .mixed<WoundDescriptionType>()
    .when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf(
          [WoundDescriptionType.WHITE, WoundDescriptionType.ROSY, WoundDescriptionType.REDDENED],
          'Angabe erforderlich',
        )
        .required('Angabe erforderlich'),
      otherwise: yup
        .mixed()
        .oneOf([
          WoundDescriptionType.WHITE,
          WoundDescriptionType.ROSY,
          WoundDescriptionType.REDDENED,
          null,
        ]),
    })
    .nullable(),
  surroundingB: yup
    .mixed<WoundDescriptionType>()
    .when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY, null]),
    })
    .nullable(),
  borderA: yup
    .mixed<WoundDescriptionType>()
    .when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf([WoundDescriptionType.ROSY, WoundDescriptionType.RED], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([WoundDescriptionType.ROSY, WoundDescriptionType.RED, null]),
    })
    .nullable(),
  borderB: yup
    .mixed<WoundDescriptionType>()
    .when('description', {
      is: (value: string) => !value,
      then: yup
        .mixed()
        .oneOf([WoundDescriptionType.SOFT, WoundDescriptionType.HARD], 'Angabe erforderlich')
        .required('Angabe erforderlich'),
      otherwise: yup.mixed().oneOf([WoundDescriptionType.SOFT, WoundDescriptionType.HARD, null]),
    })
    .nullable(),
  borderPainful: yup.boolean().default(false).nullable(),
  borderSublime: yup.boolean().default(false).nullable(),
  exsudate: yup.boolean().default(false).nullable(),
  exsudateDescriptionA: yup
    .mixed<WoundDescriptionType | null>()
    .when('exsudate', {
      is: true,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup
          .mixed()
          .oneOf([WoundDescriptionType.LITTLE, WoundDescriptionType.MUCH], 'Angabe erforderlich')
          .required('Angabe erforderlich'),
        otherwise: yup
          .mixed()
          .oneOf([WoundDescriptionType.LITTLE, WoundDescriptionType.MUCH, null]),
      }),
      otherwise: yup.mixed().oneOf([null]),
    })
    .nullable(),
  exsudateDescriptionB: yup
    .mixed<WoundDescriptionType | null>()
    .when('exsudate', {
      is: true,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup
          .mixed()
          .oneOf(
            [WoundDescriptionType.CLEAR, WoundDescriptionType.SERIOUS, WoundDescriptionType.BLOODY],
            'Angabe erforderlich',
          )
          .required('Angabe erforderlich'),
        otherwise: yup
          .mixed()
          .oneOf([
            WoundDescriptionType.CLEAR,
            WoundDescriptionType.SERIOUS,
            WoundDescriptionType.BLOODY,
            null,
          ]),
      }),
      otherwise: yup.mixed().oneOf([null]),
    })
    .nullable(),
  necrosis: yup.boolean().default(false),
  necrosisDescriptionA: yup
    .mixed<WoundDescriptionType>()
    .when('necrosis', {
      is: true,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup
          .mixed()
          .oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY], 'Angabe erforderlich')
          .required('Angabe erforderlich'),
        otherwise: yup.mixed().oneOf([WoundDescriptionType.WET, WoundDescriptionType.DRY, null]),
      }),
      otherwise: yup.mixed().oneOf([null]),
    })
    .nullable(),
  necrosisDescriptionB: yup
    .mixed<WoundDescriptionType>()
    .when('necrosis', {
      is: true,
      then: yup.mixed().when('description', {
        is: (value: string) => !value,
        then: yup
          .mixed()
          .oneOf(
            [WoundDescriptionType.PARTIALLY, WoundDescriptionType.COMPLETE],
            'Angabe erforderlich',
          )
          .required('Angabe erforderlich'),
        otherwise: yup
          .mixed()
          .oneOf([WoundDescriptionType.PARTIALLY, WoundDescriptionType.COMPLETE, null]),
      }),
      otherwise: yup.mixed().nullable(),
    })
    .nullable(),
  foulSmelling: yup.boolean().default(false),
  note: yup.string().nullable(),
  documentedAt: yup.date().nullable(),
});

export interface IWoundDescription {
  dimensionLength: number;
  woundType: WoundType;
  dimensionsWidth: number;
  dimensionsDepth: number;
  decubitusStadium: DecubitusStadium;
  decubitusStadiumDescription: string;
  ulcusGrade: UlcusGrade;
  footSyndromGrade: FootSyndromGrade;
  surroundingA: WoundDescriptionType;
  surroundingB: WoundDescriptionType;
  borderA: WoundDescriptionType;
  borderB: WoundDescriptionType;
  borderPainful: boolean;
  borderSublime: boolean;
  woundBedA: WoundDescriptionType;
  woundBedB: WoundDescriptionType;
  exsudate: boolean;
  exsudateDescriptionA: WoundDescriptionType;
  exsudateDescriptionB: WoundDescriptionType;
  necrosis: boolean;
  necrosisDescriptionA: WoundDescriptionType;
  necrosisDescriptionB: WoundDescriptionType;
  foulSmelling: boolean;
  note: string;
  description: string;
  documentedAt: Date;
}
